import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { Header, Image, Button, Label, Message, Loader, Container, List, Icon } from 'semantic-ui-react';
import styles from './PostReviews.module.css';

const COMMUNITY_GROUP_ID = 2;

// postsForReview returns the Post union; fields must be selected per member.
const POST_LIST_FIELDS = `
  id
  title
  type
  createdAt
  publishAt
  tags { id name groupId }
  poster { id name photoUrl }
`;

const GET_POSTS_FOR_REVIEW = gql`
  query PostsForReview {
    postsForReview {
      ... on CommunityUpdate { ${POST_LIST_FIELDS} }
      ... on CommunityPrayerRequest { ${POST_LIST_FIELDS} }
      ... on LeadershipUpdate { ${POST_LIST_FIELDS} }
      ... on LeadershipPrayerRequest { ${POST_LIST_FIELDS} }
      ... on Birthday { ${POST_LIST_FIELDS} }
    }
  }
`;

const PostReviewsView: React.FC = () => {
    const navigate = useNavigate();
    const { data, loading, error } = useQuery(GET_POSTS_FOR_REVIEW, { fetchPolicy: 'network-only' });

    if (loading) return <Loader active inline='centered' style={{ marginTop: '5em' }}>Loading queue...</Loader>;
    if (error) return <Container text><Message error content="Could not fetch the review queue." /></Container>;

    const posts = data?.postsForReview || [];

    const formatPublishTime = (publishAt: number) => {
        const publishDate = new Date(publishAt);
        return publishDate <= new Date()
            ? <span className={styles.liveNow}>Immediately</span>
            : publishDate.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
    };

    return (
        <Container className={styles.pageContainer}>
            <div className={styles.headerSection}>
                <Header as="h1">Review Queue</Header>
                <Label color="orange" size="tiny">{posts.length} Pending</Label>
            </div>

            <List relaxed className={styles.postList}>
                {posts.map((post: any) => {
                    // Logic to separate Community tags from standard tags
                    const communityTags = post.tags?.filter((t: any) => t.groupId === COMMUNITY_GROUP_ID) || [];
                    const otherTags = post.tags?.filter((t: any) => t.groupId !== COMMUNITY_GROUP_ID) || [];

                    const hasCommunities = communityTags.length > 0;

                    return (
                        <List.Item key={post.id} className={styles.postItem}>
                            {/* LEFT: Identity & Categorization */}
                            <div className={styles.leftCol}>
                                <div style={{ marginBottom: '4px' }}>
                                    <Label size="mini" basic className={styles.typeBadge}>{post.type}</Label>
                                </div>
                                <div className={styles.postTitle} style={{ marginBottom: '8px' }}>{post.title}</div>

                                <div className={styles.tagSection}>
                                    {hasCommunities ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '0.85em', fontWeight: 'bold', color: '#666' }}>
                                                Communities:
                                            </span>
                                            {communityTags.map((tag: any) => (
                                                <Label key={tag.id} size="mini" color="purple" basic>
                                                    <Icon name="users" />
                                                    {tag.name}
                                                </Label>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {otherTags.map((tag: any) => (
                                                <Label key={tag.id} size="mini" color="teal" basic>
                                                    <Icon name="tag" />
                                                    {tag.name}
                                                </Label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* MIDDLE: Timeline */}
                            <div className={styles.centerCol}>
                                <div className={styles.dateRow}>
                                    <Icon name="edit outline" size="small" />
                                    <span>
                                        Drafted {new Date(Number(post.createdAt)).toLocaleString([], {
                                            dateStyle: 'short',
                                            timeStyle: 'short'
                                        })}
                                    </span>
                                </div>
                                <div className={styles.dateRow}>
                                    <Icon name="send" size="small" />
                                    <span>Goes live: {formatPublishTime(Number(post.publishAt))}</span>
                                </div>
                            </div>

                            {/* RIGHT: User & Action */}
                            <div className={styles.rightCol}>
                                <div className={styles.avatarWrapper}>
                                    <Image avatar src={post.poster.photoUrl} className={styles.posterAvatar} />
                                    <span className={styles.hoverName}>{post.poster.name}</span>
                                </div>
                                <Button
                                    circular
                                    icon="chevron right"
                                    onClick={() => navigate(`/posts/reviews/${post.id}`)}
                                    className={styles.reviewBtn}
                                />
                            </div>
                        </List.Item>
                    );
                })}
            </List>
        </Container>
    );
};

export { PostReviewsView };
